import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

import { incrementQuantity, decrementQuantity, removeItem } from '../../redux/cartSlice';
import QuantitySelector from '../../components/Buttons/QuantitySelector';
import sendOrderEmail from '../../services/sendorder';

const FullPanier = () => {
  const { t } = useTranslation();

  const cart = useSelector((state) => state.cart);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', object: '' });
  const [orderNote, setOrderNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleQuantityChange = (item, newQuantity) => {
    const quantityDiff = newQuantity - item.quantity;
    if (quantityDiff > 0) {
      for (let i = 0; i < quantityDiff; i++) dispatch(incrementQuantity(item.id));
    } else if (quantityDiff < 0) {
      for (let i = 0; i < -quantityDiff; i++) dispatch(decrementQuantity(item.id));
    }
  };

  const calculateTotal = () =>
    cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);

  const validateForm = () => {
    const { name, email } = formData;
    if (!name || !email) {
      setFeedbackMessage({ type: 'error', message: t('cart.form.validation.missing') });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFeedbackMessage({ type: 'error', message: t('cart.form.validation.invalid_email') });
      return false;
    }
    return true;
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      setFeedbackMessage({ type: 'error', message: t('cart.empty.warning') });
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    setFeedbackMessage({});

    const total = calculateTotal();
    try {
      const result = await sendOrderEmail(cart, total, orderNote, formData, t);
      if (result.success) {
        setFeedbackMessage({ type: 'success', message: result.message });
        setOrderNote('');
        setFormData({ name: '', email: '', phone: '', object: '' });
      } else {
        setFeedbackMessage({ type: 'error', message: result.message });
      }
    } catch (error) {
      setFeedbackMessage({ type: 'error', message: "Une erreur s'est produite lors de l'envoi de la commande." });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition";

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>Votre Panier | Au Coing du Ventoux</title>
        <meta name="description" content="Consultez votre panier et finalisez votre commande d'alcool de coing artisanal." />
        <meta property="og:title" content="Votre Panier | Au Coing du Ventoux" />
        <meta property="og:url" content="https://aucoingduventoux.com/fullpanier" />
        <link rel="canonical" href="https://aucoingduventoux.com/fullpanier" />
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 md:px-8 pt-40 md:pt-52 pb-24">
        {/* En-tete */}
        <div className="text-center mb-12">
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
            Commande
          </span>
          <h1 className="font-sans mt-4 text-4xl md:text-5xl text-stone-900">{t('cart.title')}</h1>
        </div>

        {cart.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-nunito text-lg text-stone-500 mb-8">{t('cart.empty')}</p>
            <Link
              to="/boutique"
              className="inline-block font-sans tracking-wide rounded-xl px-8 py-3.5 text-white transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
              style={{ backgroundColor: '#2E2318' }}
            >
              {t('home.button.shop')}
            </Link>
          </div>
        ) : (
          <>
            {/* Liste des articles */}
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: 'rgba(120,90,40,0.15)' }}>
              {cart.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-wrap items-center gap-4 md:gap-6 p-5 md:p-6"
                  style={{ borderBottom: index < cart.length - 1 ? '1px solid rgba(120,90,40,0.12)' : 'none' }}
                >
                  {/* Image */}
                  <div className="flex items-center justify-center rounded-xl p-2" style={{ backgroundColor: '#F4ECDD' }}>
                    <img src={item.image} alt={item.title} className="w-16 h-24 md:w-20 md:h-28 object-contain" />
                  </div>

                  {/* Titre + prix unitaire + quantite */}
                  <div className="flex-1 min-w-[160px]">
                    <h3 className="font-sans text-base md:text-lg text-stone-900">{item.title}</h3>
                    <p className="font-nunito text-sm text-stone-500 mt-1">{item.price.toFixed(2)} €</p>
                    <div className="inline-flex mt-3">
                      <QuantitySelector
                        onQuantityChange={(newQuantity) => handleQuantityChange(item, newQuantity)}
                        showInput={true}
                        initialQuantity={item.quantity}
                      />
                    </div>
                  </div>

                  {/* Total ligne + supprimer */}
                  <div className="text-right ml-auto">
                    <p className="font-sans text-xl" style={{ color: '#A9760F' }}>
                      {(item.price * item.quantity).toFixed(2)} €
                    </p>
                    <button
                      className="mt-2 font-nunito text-sm text-stone-400 hover:text-red-600 transition-colors"
                      onClick={() => dispatch(removeItem(item.id))}
                    >
                      {t('cart.remove')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Bas : infos + formulaire */}
            <div className="flex flex-col md:flex-row mt-10 gap-8">
              {/* Infos commande */}
              <div className="w-full md:w-1/2">
                <div className="rounded-2xl p-6 h-full" style={{ backgroundColor: '#F4ECDD' }}>
                  <h2 className="font-sans text-lg md:text-xl text-stone-900 mb-4">
                    {t('cart.payment_info.title')}
                  </h2>
                  <ul className="space-y-3 font-nunito text-stone-600">
                    <li className="flex gap-2"><span style={{ color: '#C8951F' }}>•</span>{t('cart.payment_info.note1')}</li>
                    <li className="flex gap-2"><span style={{ color: '#C8951F' }}>•</span>{t('cart.payment_info.note2')}</li>
                    <li className="flex gap-2"><span style={{ color: '#C8951F' }}>•</span>{t('cart.payment_info.note3')}</li>
                  </ul>
                  <div className="mt-6 pt-6 border-t flex items-center justify-between" style={{ borderColor: 'rgba(120,90,40,0.15)' }}>
                    <span className="font-sans text-lg text-stone-700">{t('cart.order.total')}</span>
                    <span className="font-sans text-3xl" style={{ color: '#A9760F' }}>{calculateTotal()} €</span>
                  </div>
                </div>
              </div>

              {/* Formulaire */}
              <div className="w-full md:w-1/2">
                <form onSubmit={handleSubmitOrder} className="space-y-4">
                  <input type="text" name="name" placeholder={t('cart.form.name')} value={formData.name} onChange={handleChange} className={inputClass} required />
                  <input type="email" name="email" placeholder={t('cart.form.email')} value={formData.email} onChange={handleChange} className={inputClass} required />
                  <input type="text" name="phone" placeholder={t('cart.form.phone')} value={formData.phone} onChange={handleChange} className={inputClass} />

                  <select name="object" id="object" value={formData.object} onChange={handleChange} className={inputClass} required>
                    <option value="" disabled>{t('cart.form.object')}</option>
                    <option value="particulier">{t('cart.form.object.particulier')}</option>
                    <option value="professionnel">{t('cart.form.object.professionnel')}</option>
                    <option value="autre">{t('cart.form.object.autre')}</option>
                  </select>

                  <div>
                    <label htmlFor="orderNote" className="block mb-2 font-nunito text-sm text-stone-600">
                      {t('cart.form.note')}
                    </label>
                    <textarea
                      id="orderNote"
                      value={orderNote}
                      onChange={(e) => setOrderNote(e.target.value)}
                      className={inputClass}
                      rows="4"
                    />
                  </div>

                  {feedbackMessage.message && (
                    <div
                      className="rounded-xl p-4 font-nunito text-sm"
                      style={
                        feedbackMessage.type === 'error'
                          ? { backgroundColor: 'rgba(180,69,46,0.1)', color: '#B4452E' }
                          : { backgroundColor: 'rgba(79,122,58,0.12)', color: '#3F6230' }
                      }
                    >
                      {feedbackMessage.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full font-sans tracking-wide rounded-xl py-4 text-white transition-all duration-300 ${
                      loading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 hover:scale-[1.01]'
                    }`}
                    style={{ backgroundColor: '#2E2318' }}
                  >
                    {loading ? t('cart.form.sending') : t('cart.form.submit')}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FullPanier;